/**
 * Round Events Timeline
 *
 * Shared widget used in both the HnS round setup form and the live HnS
 * round card. Renders the timeline of scheduled/fired Round Events (Marco
 * Polo + Ruleset Change) and provides forms to add new events, delete
 * scheduled ones, and — in live mode — apply a ruleset change retroactively.
 *
 * The widget is pure: it renders whatever `events` array it's given and
 * emits callbacks. Setup consumers keep an in-memory draft list; live
 * consumers wire the callbacks to socket emissions.
 */

import { memo, useCallback, useMemo, useState } from 'react';
import { statusBadgeClass } from '@swtorbit/shared-components';
import {
  RoundEvent,
  RoundEventKind,
  RoundEventPayload,
  RulesetChangePayload,
  HnsRoleRules,
  DEFAULT_HNS_ROLE_RULES,
  HNS_RULE_KEYS,
  formatHnsRuleAllowance,
  isRulesetChangePayload,
  ROUND_EVENT_MIN_GAP_SECONDS,
  ROUND_EVENT_LEAD_TIME_MIN_SECONDS,
  ROUND_EVENT_LEAD_TIME_MAX_SECONDS,
} from '@swtorbit/shared-socket-events';
import { RoleRulesEditor } from './RoleRulesEditor';

export interface RoundEventDraft {
  kind: RoundEventKind;
  scheduledOffsetSeconds: number;
  leadTimeSeconds?: number;
  payload: RoundEventPayload;
}

export interface RoundEventsTimelineProps {
  mode: 'setup' | 'live';
  events: RoundEvent[];
  /** Baseline hider rules used for "copy from current" and diff badges. */
  baselineHiderRules: HnsRoleRules;
  baselineSeekerRules: HnsRoleRules;
  onSchedule: (draft: RoundEventDraft) => void;
  onDelete?: (eventId: string) => void;
  onApplyNow?: (payload: RulesetChangePayload) => void;
  /** Hide add/delete controls (e.g. round already completed). */
  disabled?: boolean;
}

function formatOffset(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

function parseOffset(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const m = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (!m) return null;
  const mins = Number(m[1]);
  const secs = Number(m[2]);
  if (secs >= 60) return null;
  return mins * 60 + secs;
}

/**
 * Compute the resolved ruleset at a given offset by replaying scheduled
 * (non-cancelled) ruleset changes with a smaller offset than `atOffset`.
 * Used to prefill "copy from current" and to compute diff badges.
 */
function resolveRulesAtOffset(
  events: RoundEvent[],
  baseHider: HnsRoleRules,
  baseSeeker: HnsRoleRules,
  atOffset: number
): RulesetChangePayload {
  const relevant = events
    .filter(e =>
      e.kind === RoundEventKind.RULESET_CHANGE &&
      e.status !== 'cancelled' &&
      e.scheduledOffsetSeconds !== null &&
      e.scheduledOffsetSeconds < atOffset &&
      isRulesetChangePayload(e.payload)
    )
    .sort((a, b) => (a.scheduledOffsetSeconds ?? 0) - (b.scheduledOffsetSeconds ?? 0));

  if (relevant.length === 0) {
    return { hiderRules: { ...baseHider }, seekerRules: { ...baseSeeker } };
  }
  const last = relevant[relevant.length - 1].payload as RulesetChangePayload;
  return { hiderRules: { ...last.hiderRules }, seekerRules: { ...last.seekerRules } };
}

function diffRules(prev: HnsRoleRules, next: HnsRoleRules): string[] {
  const diffs: string[] = [];
  for (const { key, label } of HNS_RULE_KEYS) {
    if (prev[key] !== next[key]) {
      diffs.push(`${label}: ${formatHnsRuleAllowance(prev[key], key)} → ${formatHnsRuleAllowance(next[key], key)}`);
    }
  }
  return diffs;
}

export const RoundEventsTimeline = memo(function RoundEventsTimeline({
  mode,
  events,
  baselineHiderRules,
  baselineSeekerRules,
  onSchedule,
  onDelete,
  onApplyNow,
  disabled = false,
}: RoundEventsTimelineProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [isApplyingNow, setIsApplyingNow] = useState(false);
  const [addKind, setAddKind] = useState<RoundEventKind>(RoundEventKind.RULESET_CHANGE);
  const [addOffset, setAddOffset] = useState<string>('02:00');
  const [addLeadTime, setAddLeadTime] = useState<number>(10);
  const [addHiderRules, setAddHiderRules] = useState<HnsRoleRules>({ ...baselineHiderRules });
  const [addSeekerRules, setAddSeekerRules] = useState<HnsRoleRules>({ ...baselineSeekerRules });
  const [applyNowHiderRules, setApplyNowHiderRules] = useState<HnsRoleRules>({ ...baselineHiderRules });
  const [applyNowSeekerRules, setApplyNowSeekerRules] = useState<HnsRoleRules>({ ...baselineSeekerRules });
  const [addError, setAddError] = useState<string | null>(null);

  // Sort events for display: retroactive-fired by firedAt asc, scheduled by offset asc.
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aKey = a.scheduledOffsetSeconds ?? (a.firedAt ?? 0) / 1000;
      const bKey = b.scheduledOffsetSeconds ?? (b.firedAt ?? 0) / 1000;
      return aKey - bKey;
    });
  }, [events]);

  const scheduledRulesetChanges = useMemo(() => (
    events.filter(e =>
      e.kind === RoundEventKind.RULESET_CHANGE &&
      e.status === 'scheduled' &&
      !e.isRetroactive &&
      e.scheduledOffsetSeconds !== null
    )
  ), [events]);

  const openAddForm = useCallback(() => {
    // Prefill with the ruleset that would be effective just before the
    // default offset, so the admin sees the current state and only ticks
    // what should change.
    const preOffset = parseOffset(addOffset) ?? 120;
    const resolved = resolveRulesAtOffset(events, baselineHiderRules, baselineSeekerRules, preOffset + 0.001);
    setAddHiderRules({ ...resolved.hiderRules });
    setAddSeekerRules({ ...resolved.seekerRules });
    setAddError(null);
    setIsAdding(true);
  }, [addOffset, events, baselineHiderRules, baselineSeekerRules]);

  const copyFromCurrent = useCallback(() => {
    const off = parseOffset(addOffset) ?? 0;
    const resolved = resolveRulesAtOffset(events, baselineHiderRules, baselineSeekerRules, off + 0.001);
    setAddHiderRules({ ...resolved.hiderRules });
    setAddSeekerRules({ ...resolved.seekerRules });
  }, [addOffset, events, baselineHiderRules, baselineSeekerRules]);

  const submitAdd = useCallback(() => {
    const offset = parseOffset(addOffset);
    if (offset === null || offset < 0) {
      setAddError('Offset must be mm:ss or seconds (>= 0)');
      return;
    }

    if (addKind === RoundEventKind.RULESET_CHANGE) {
      // 2-min buffer check against all other scheduled non-retroactive ruleset changes
      const conflicting = scheduledRulesetChanges.find(e =>
        Math.abs((e.scheduledOffsetSeconds ?? 0) - offset) < ROUND_EVENT_MIN_GAP_SECONDS
      );
      if (conflicting) {
        setAddError(`Ruleset changes must be at least ${ROUND_EVENT_MIN_GAP_SECONDS / 60} minutes apart`);
        return;
      }
      if (
        addLeadTime < ROUND_EVENT_LEAD_TIME_MIN_SECONDS ||
        addLeadTime > ROUND_EVENT_LEAD_TIME_MAX_SECONDS
      ) {
        setAddError(`Lead time must be ${ROUND_EVENT_LEAD_TIME_MIN_SECONDS}–${ROUND_EVENT_LEAD_TIME_MAX_SECONDS}s`);
        return;
      }
    }

    const payload: RoundEventPayload = addKind === RoundEventKind.RULESET_CHANGE
      ? { hiderRules: addHiderRules, seekerRules: addSeekerRules }
      : {};

    onSchedule({
      kind: addKind,
      scheduledOffsetSeconds: offset,
      leadTimeSeconds: addKind === RoundEventKind.RULESET_CHANGE ? addLeadTime : undefined,
      payload,
    });

    setIsAdding(false);
    setAddError(null);
  }, [
    addOffset, addKind, addLeadTime, addHiderRules, addSeekerRules,
    scheduledRulesetChanges, onSchedule,
  ]);

  const openApplyNow = useCallback(() => {
    // Prefill with the currently-effective rules (last fired ruleset change,
    // or baseline). Uses a very large "atOffset" so all past changes apply.
    const resolved = resolveRulesAtOffset(events, baselineHiderRules, baselineSeekerRules, Number.MAX_SAFE_INTEGER);
    setApplyNowHiderRules({ ...resolved.hiderRules });
    setApplyNowSeekerRules({ ...resolved.seekerRules });
    setIsApplyingNow(true);
  }, [events, baselineHiderRules, baselineSeekerRules]);

  const submitApplyNow = useCallback(() => {
    if (!onApplyNow) return;
    onApplyNow({ hiderRules: applyNowHiderRules, seekerRules: applyNowSeekerRules });
    setIsApplyingNow(false);
  }, [onApplyNow, applyNowHiderRules, applyNowSeekerRules]);

  const renderRuleEditor = (
    hider: HnsRoleRules,
    seeker: HnsRoleRules,
    setHider: (r: HnsRoleRules) => void,
    setSeeker: (r: HnsRoleRules) => void,
  ) => (
    <RoleRulesEditor
      hiderRules={hider}
      seekerRules={seeker}
      onHiderRulesChange={setHider}
      onSeekerRulesChange={setSeeker}
    />
  );

  return (
    <div className="d-flex flex-column gap-xs">
      <div className="d-flex gap-xs align-items-center flex-wrap">
        <strong>Round Events Timeline</strong>
        <span className="text-muted">({sortedEvents.length})</span>
        <div className="d-flex gap-2xs flex-wrap" style={{ marginLeft: 'auto' }}>
          {!disabled && !isAdding && (
            <button className="btn btn--aqua btn--sm" onClick={openAddForm}>
              + Add Event
            </button>
          )}
          {!disabled && mode === 'live' && onApplyNow && !isApplyingNow && (
            <button className="btn btn--amber btn--sm" onClick={openApplyNow}>
              ⚡ Change Rules Now
            </button>
          )}
        </div>
      </div>

      {sortedEvents.length === 0 && !isAdding && (
        <p className="text-muted">No events scheduled.</p>
      )}

      {sortedEvents.length > 0 && (
        <div className="d-flex flex-column gap-2xs">
          {sortedEvents.map((event) => {
            const isRuleset = event.kind === RoundEventKind.RULESET_CHANGE;
            const kindIcon = isRuleset ? '🔄' : '📍';
            const kindLabel = isRuleset ? 'Ruleset Change' : 'Marco Polo';
            const offsetLabel = event.isRetroactive
              ? 'On the fly'
              : event.scheduledOffsetSeconds !== null
                ? `T+${formatOffset(event.scheduledOffsetSeconds)}`
                : '—';

            let diffs: string[] = [];
            if (isRuleset && isRulesetChangePayload(event.payload)) {
              const prev = resolveRulesAtOffset(
                events.filter(e => e.id !== event.id),
                baselineHiderRules,
                baselineSeekerRules,
                event.scheduledOffsetSeconds ?? Number.MAX_SAFE_INTEGER
              );
              const hiderDiffs = diffRules(prev.hiderRules, event.payload.hiderRules)
                .map(d => `${d} (H)`);
              const seekerDiffs = diffRules(prev.seekerRules, event.payload.seekerRules)
                .map(d => `${d} (S)`);
              diffs = [...hiderDiffs, ...seekerDiffs];
            }

            const canDelete = !disabled && onDelete && event.status === 'scheduled';

            return (
              <div key={event.id} className="card card--xs card--bg-midnight card--border card--border-navy">
                <span style={{ fontSize: '1.25em' }}>{kindIcon}</span>
                <span style={{ minWidth: '4.5rem' }}><strong>{offsetLabel}</strong></span>
                <span>{kindLabel}</span>
                {event.leadTimeSeconds && isRuleset && (
                  <span className="text-muted">(warn {event.leadTimeSeconds}s)</span>
                )}
                <span className={statusBadgeClass(event.status)}>{event.status}</span>
                {event.isRetroactive && (
                  <span className="badge">retroactive</span>
                )}
                {diffs.length > 0 && (
                  <span className="d-flex gap-2xs flex-wrap">
                    {diffs.map((d, i) => (
                      <span key={i} className="badge">{d}</span>
                    ))}
                  </span>
                )}
                {canDelete && (
                  <button
                    className="btn btn--red btn--sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => onDelete!(event.id)}
                    title="Delete event"
                  >
                    🗑
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isAdding && (
        <div className="card card--xs card--bg-midnight card--border card--border-navy d-flex flex-column gap-xs" style={{ alignItems: 'stretch' }}>
          <div className="d-flex gap-xs align-items-center flex-wrap">
            <label className="d-flex align-items-center gap-2xs">
              <span>Kind:</span>
              <select
                className="select"
                value={addKind}
                onChange={(e) => setAddKind(e.target.value as RoundEventKind)}
              >
                <option value={RoundEventKind.RULESET_CHANGE}>Ruleset Change</option>
                <option value={RoundEventKind.MARCO_POLO}>Marco Polo</option>
              </select>
            </label>
            <label className="d-flex align-items-center gap-2xs">
              <span>At (mm:ss):</span>
              <input
                className="input"
                type="text"
                value={addOffset}
                onChange={(e) => setAddOffset(e.target.value)}
                style={{ width: '5rem' }}
              />
            </label>
            {addKind === RoundEventKind.RULESET_CHANGE && (
              <label className="d-flex align-items-center gap-2xs">
                <span>Warn {addLeadTime}s before:</span>
                <input
                  className="range"
                  type="range"
                  min={ROUND_EVENT_LEAD_TIME_MIN_SECONDS}
                  max={ROUND_EVENT_LEAD_TIME_MAX_SECONDS}
                  value={addLeadTime}
                  onChange={(e) => setAddLeadTime(Number(e.target.value))}
                />
              </label>
            )}
          </div>

          {addKind === RoundEventKind.RULESET_CHANGE && (
            <details open>
              <summary className="d-flex align-items-center gap-xs" style={{ cursor: 'pointer' }}>
                <strong>Rules to apply</strong>
                <button
                  type="button"
                  className="btn btn--outline btn--slate btn--sm"
                  onClick={(e) => { e.preventDefault(); copyFromCurrent(); }}
                >
                  Copy from Current
                </button>
              </summary>
              {renderRuleEditor(addHiderRules, addSeekerRules, setAddHiderRules, setAddSeekerRules)}
            </details>
          )}

          {addError && (
            <p className="text-red">{addError}</p>
          )}

          <div className="d-flex gap-xs justify-content-end">
            <button className="btn btn--outline btn--slate btn--sm" onClick={() => { setIsAdding(false); setAddError(null); }}>
              Cancel
            </button>
            <button className="btn btn--green btn--sm" onClick={submitAdd}>
              {mode === 'live' ? 'Schedule' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {isApplyingNow && mode === 'live' && onApplyNow && (
        <div className="card card--xs card--bg-midnight card--border card--border-navy d-flex flex-column gap-xs" style={{ alignItems: 'stretch' }}>
          <strong>Change Rules Now (retroactive, bypasses 2-min buffer)</strong>
          <details open>
            <summary style={{ cursor: 'pointer' }}><strong>Rules to apply</strong></summary>
            {renderRuleEditor(
              applyNowHiderRules,
              applyNowSeekerRules,
              setApplyNowHiderRules,
              setApplyNowSeekerRules,
            )}
          </details>
          <div className="d-flex gap-xs justify-content-end">
            <button className="btn btn--outline btn--slate btn--sm" onClick={() => setIsApplyingNow(false)}>
              Cancel
            </button>
            <button className="btn btn--amber btn--sm" onClick={submitApplyNow}>
              Apply Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Convenience helper for setup-mode consumers: turn a draft into a synthetic
 * RoundEvent so the timeline can render pre-persistence entries uniformly.
 */
export function draftToPseudoEvent(
  draft: RoundEventDraft,
  index: number
): RoundEvent {
  const now = Date.now();
  const payload = draft.kind === RoundEventKind.RULESET_CHANGE
    ? draft.payload
    : draft.payload ?? {};
  return {
    id: `draft-${index}-${now}`,
    roomCode: '',
    roundIndex: 0,
    kind: draft.kind,
    status: 'scheduled',
    scheduledOffsetSeconds: draft.scheduledOffsetSeconds,
    effectiveAt: null,
    leadTimeSeconds: draft.leadTimeSeconds,
    isRetroactive: false,
    payload: payload as RoundEventPayload,
    createdAt: now,
    updatedAt: now,
  };
}

// Re-export used constants for callers building drafts.
export { DEFAULT_HNS_ROLE_RULES };
